import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import type { AdvancedSkillAssessment } from '@coffeeplexity/shared';
import { ProgressCircle } from './ProgressCircle';

// const { width } = Dimensions.get('window');

interface AdvancedSkillAssessmentViewProps {
  assessment: AdvancedSkillAssessment;
}

export const AdvancedSkillAssessmentView: React.FC<
  AdvancedSkillAssessmentViewProps
> = ({ assessment }) => {
  const [activeTab, setActiveTab] = useState<
    'overview' | 'competencies' | 'gaps' | 'strengths' | 'plan'
  >('overview');

  const renderTabBar = () => (
    <View style={styles.tabBar}>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.tabScrollContent}
      >
        {[
          { id: 'overview', title: 'Overview', icon: 'analytics-outline' },
          { id: 'competencies', title: 'Skills', icon: 'library-outline' },
          { id: 'gaps', title: 'Gaps', icon: 'warning-outline' },
          { id: 'strengths', title: 'Strengths', icon: 'trophy-outline' },
          { id: 'plan', title: 'Plan', icon: 'map-outline' },
        ].map(tab => (
          <TouchableOpacity
            key={tab.id}
            style={[styles.tab, activeTab === tab.id && styles.activeTab]}
            onPress={() => setActiveTab(tab.id as any)}
          >
            <Ionicons
              name={tab.icon as any}
              size={16}
              color={activeTab === tab.id ? '#6750A4' : '#666'}
            />
            <Text
              style={[
                styles.tabText,
                activeTab === tab.id && styles.activeTabText,
              ]}
            >
              {tab.title}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  const renderOverview = () => (
    <View style={styles.overviewContainer}>
      <View style={styles.scoreCard}>
        <View style={styles.scoreCircleContainer}>
          <ProgressCircle
            progress={assessment.overallScore}
            size={100}
            strokeWidth={8}
            color='#6750A4'
          />
          <View style={styles.scoreCenter}>
            <Text style={styles.scoreValue}>{assessment.overallScore}</Text>
            <Text style={styles.scoreLabel}>Overall</Text>
          </View>
        </View>

        <View style={styles.scoreDetails}>
          <View style={styles.scoreDetail}>
            <Text style={styles.scoreDetailValue}>
              {assessment.competencyMap.length}
            </Text>
            <Text style={styles.scoreDetailLabel}>Competencies</Text>
          </View>
          <View style={styles.scoreDetail}>
            <Text style={styles.scoreDetailValue}>
              {assessment.skillGaps.length}
            </Text>
            <Text style={styles.scoreDetailLabel}>Skill Gaps</Text>
          </View>
          <View style={styles.scoreDetail}>
            <Text style={styles.scoreDetailValue}>
              {assessment.strengthAreas.length}
            </Text>
            <Text style={styles.scoreDetailLabel}>Strengths</Text>
          </View>
        </View>
      </View>

      <View style={styles.progressCard}>
        <Text style={styles.cardTitle}>Progress Since Last Assessment</Text>
        <View style={styles.progressIndicator}>
          <Text style={styles.progressValue}>
            +{assessment.progressSinceLastAssessment}%
          </Text>
          <Ionicons name='trending-up' size={20} color='#10B981' />
        </View>
        <Text style={styles.progressDate}>
          Last assessed: {new Date(assessment.completedAt).toLocaleDateString()}
        </Text>
        <Text style={styles.nextAssessment}>
          Next assessment:{' '}
          {new Date(assessment.nextAssessmentDate).toLocaleDateString()}
        </Text>
      </View>
    </View>
  );

  const renderCompetencies = () => (
    <View style={styles.competenciesContainer}>
      {assessment.competencyMap.map(competency => (
        <View key={competency.competencyId} style={styles.competencyCard}>
          <View style={styles.competencyHeader}>
            <Text style={styles.competencyName}>
              {competency.competencyName}
            </Text>
            <View
              style={[
                styles.priorityBadge,
                { backgroundColor: getPriorityColor(competency.priority) },
              ]}
            >
              <Text style={styles.priorityText}>
                {competency.priority.toUpperCase()}
              </Text>
            </View>
          </View>

          <Text style={styles.competencyCategory}>{competency.category}</Text>
          <Text style={styles.competencyDescription}>
            {competency.proficiencyDescription}
          </Text>

          <View style={styles.levelContainer}>
            <View style={styles.levelBar}>
              <View style={styles.levelBackground}>
                <View
                  style={[
                    styles.levelFill,
                    { width: `${competency.currentLevel}%` },
                  ]}
                />
                <View
                  style={[
                    styles.targetIndicator,
                    { left: `${competency.targetLevel}%` },
                  ]}
                />
              </View>
            </View>
            <View style={styles.levelLabels}>
              <Text style={styles.currentLevel}>
                Current: {competency.currentLevel}%
              </Text>
              <Text style={styles.targetLevel}>
                Target: {competency.targetLevel}%
              </Text>
            </View>
          </View>

          <View style={styles.timeToTarget}>
            <Ionicons name='time-outline' size={16} color='#6B7280' />
            <Text style={styles.timeToTargetText}>
              {competency.timeToTarget} days to target
            </Text>
          </View>

          {competency.developmentNeeds.length > 0 && (
            <View style={styles.developmentNeeds}>
              <Text style={styles.developmentTitle}>Development Needs:</Text>
              {competency.developmentNeeds.map((need, index) => (
                <Text key={index} style={styles.developmentNeed}>
                  • {need}
                </Text>
              ))}
            </View>
          )}
        </View>
      ))}
    </View>
  );

  const renderSkillGaps = () => (
    <View style={styles.gapsContainer}>
      {assessment.skillGaps.length === 0 ? (
        <View style={styles.emptyState}>
          <Ionicons name='checkmark-circle-outline' size={64} color='#10B981' />
          <Text style={styles.emptyStateText}>No significant skill gaps!</Text>
          <Text style={styles.emptyStateSubtext}>
            You're performing well across all assessed areas
          </Text>
        </View>
      ) : (
        assessment.skillGaps.map(gap => (
          <View key={gap.skillId} style={styles.gapCard}>
            <View style={styles.gapHeader}>
              <Text style={styles.gapSkillName}>{gap.skillName}</Text>
              <View
                style={[
                  styles.impactBadge,
                  { backgroundColor: getImpactColor(gap.impact) },
                ]}
              >
                <Text style={styles.impactText}>
                  {gap.impact.toUpperCase()}
                </Text>
              </View>
            </View>

            <View style={styles.gapMetrics}>
              <View style={styles.gapMetric}>
                <Text style={styles.gapMetricLabel}>Current Level</Text>
                <Text style={styles.gapMetricValue}>{gap.currentLevel}%</Text>
              </View>
              <View style={styles.gapMetric}>
                <Text style={styles.gapMetricLabel}>Required Level</Text>
                <Text style={styles.gapMetricValue}>{gap.requiredLevel}%</Text>
              </View>
              <View style={styles.gapMetric}>
                <Text style={styles.gapMetricLabel}>Gap Size</Text>
                <Text style={[styles.gapMetricValue, { color: '#EF4444' }]}>
                  {gap.gapSize}%
                </Text>
              </View>
            </View>

            <View style={styles.gapProgress}>
              <View style={styles.gapProgressBar}>
                <View
                  style={[
                    styles.gapProgressFill,
                    {
                      width: `${(gap.currentLevel / gap.requiredLevel) * 100}%`,
                    },
                  ]}
                />
              </View>
              <Text style={styles.gapProgressText}>
                {Math.round((gap.currentLevel / gap.requiredLevel) * 100)}% to
                target
              </Text>
            </View>

            <View style={styles.timeEstimate}>
              <Ionicons name='calendar-outline' size={16} color='#6B7280' />
              <Text style={styles.timeEstimateText}>
                Estimated time to close: {gap.estimatedTimeToClose} days
              </Text>
            </View>

            <View style={styles.recommendedActions}>
              <Text style={styles.actionsTitle}>Recommended Actions:</Text>
              {gap.recommendedActions.slice(0, 3).map((action, index) => (
                <Text key={index} style={styles.actionItem}>
                  • {action}
                </Text>
              ))}
            </View>
          </View>
        ))
      )}
    </View>
  );

  const renderStrengths = () => (
    <View style={styles.strengthsContainer}>
      {assessment.strengthAreas.map(strength => (
        <View key={strength.skillId} style={styles.strengthCard}>
          <View style={styles.strengthHeader}>
            <Text style={styles.strengthSkillName}>{strength.skillName}</Text>
            <View style={styles.proficiencyScore}>
              <Text style={styles.proficiencyValue}>
                {strength.proficiencyLevel}%
              </Text>
              <Text style={styles.proficiencyLabel}>Proficiency</Text>
            </View>
          </View>

          <View style={styles.confidenceContainer}>
            <Text style={styles.confidenceLabel}>Confidence Score</Text>
            <View style={styles.confidenceBar}>
              <View
                style={[
                  styles.confidenceFill,
                  { width: `${strength.confidenceScore * 100}%` },
                ]}
              />
            </View>
            <Text style={styles.confidenceValue}>
              {Math.round(strength.confidenceScore * 100)}%
            </Text>
          </View>

          {strength.expertiseAreas.length > 0 && (
            <View style={styles.expertiseAreas}>
              <Text style={styles.expertiseTitle}>Areas of Expertise:</Text>
              <View style={styles.expertiseTags}>
                {strength.expertiseAreas.map((area, index) => (
                  <View key={index} style={styles.expertiseTag}>
                    <Text style={styles.expertiseTagText}>{area}</Text>
                  </View>
                ))}
              </View>
            </View>
          )}

          {strength.leverageOpportunities.length > 0 && (
            <View style={styles.leverageOpportunities}>
              <Text style={styles.leverageTitle}>Leverage Opportunities:</Text>
              {strength.leverageOpportunities.map((opportunity, index) => (
                <Text key={index} style={styles.leverageItem}>
                  • {opportunity}
                </Text>
              ))}
            </View>
          )}

          {strength.mentorshipPotential && (
            <View style={styles.mentorshipBadge}>
              <Ionicons name='people-outline' size={16} color='#6750A4' />
              <Text style={styles.mentorshipText}>Mentorship Potential</Text>
            </View>
          )}
        </View>
      ))}
    </View>
  );

  const renderDevelopmentPlan = () => (
    <View style={styles.planContainer}>
      <View style={styles.planOverview}>
        <Text style={styles.planTitle}>Development Plan</Text>
        <Text style={styles.planTimeframe}>
          {assessment.developmentPlan.timeframe} day plan
        </Text>
        <Text style={styles.planCreated}>
          Created:{' '}
          {new Date(assessment.developmentPlan.createdAt).toLocaleDateString()}
        </Text>
      </View>

      <View style={styles.focusAreas}>
        <Text style={styles.sectionTitle}>Focus Areas</Text>
        {assessment.developmentPlan.focusAreas.map(area => (
          <View key={area.areaId} style={styles.focusAreaCard}>
            <View style={styles.focusAreaHeader}>
              <Text style={styles.focusAreaName}>{area.name}</Text>
              <Text style={styles.focusAreaPriority}>
                Priority {area.priority}
              </Text>
            </View>
            <Text style={styles.focusAreaTarget}>
              Target improvement: {area.targetImprovement}%
            </Text>
            <Text style={styles.focusAreaTime}>
              Time allocation: {area.timeAllocation}%
            </Text>
            <View style={styles.focusAreaActivities}>
              {area.activities.map((activity, index) => (
                <Text key={index} style={styles.activityItem}>
                  • {activity}
                </Text>
              ))}
            </View>
          </View>
        ))}
      </View>

      <View style={styles.milestones}>
        <Text style={styles.sectionTitle}>Milestones</Text>
        {assessment.developmentPlan.milestones.map(milestone => (
          <View key={milestone.milestoneId} style={styles.milestoneCard}>
            <View style={styles.milestoneHeader}>
              <Text style={styles.milestoneTitle}>{milestone.title}</Text>
              <Text style={styles.milestoneProgress}>
                {milestone.progress}%
              </Text>
            </View>
            <Text style={styles.milestoneDescription}>
              {milestone.description}
            </Text>
            <Text style={styles.milestoneTarget}>
              Target: {new Date(milestone.targetDate).toLocaleDateString()}
            </Text>
            <View style={styles.milestoneProgressBar}>
              <View
                style={[
                  styles.milestoneProgressFill,
                  { width: `${milestone.progress}%` },
                ]}
              />
            </View>
          </View>
        ))}
      </View>
    </View>
  );

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return '#EF4444';
      case 'medium':
        return '#F59E0B';
      case 'low':
        return '#10B981';
      default:
        return '#6B7280';
    }
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'critical':
        return '#DC2626';
      case 'high':
        return '#EF4444';
      case 'medium':
        return '#F59E0B';
      case 'low':
        return '#10B981';
      default:
        return '#6B7280';
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverview();
      case 'competencies':
        return renderCompetencies();
      case 'gaps':
        return renderSkillGaps();
      case 'strengths':
        return renderStrengths();
      case 'plan':
        return renderDevelopmentPlan();
      default:
        return renderOverview();
    }
  };

  return (
    <View style={styles.container}>
      {renderTabBar()}
      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        {renderContent()}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8F9FA',
  },
  tabBar: {
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    paddingVertical: 8,
  },
  tabScrollContent: {
    paddingHorizontal: 16,
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    marginRight: 8,
    borderRadius: 16,
    backgroundColor: '#F3F4F6',
  },
  activeTab: {
    backgroundColor: '#EDE7F6',
  },
  tabText: {
    marginLeft: 4,
    fontSize: 12,
    fontWeight: '500',
    color: '#666',
  },
  activeTabText: {
    color: '#6750A4',
    fontWeight: '600',
  },
  content: {
    flex: 1,
    padding: 20,
  },
  overviewContainer: {
    gap: 16,
  },
  scoreCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 20,
    flexDirection: 'row',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  scoreCircleContainer: {
    position: 'relative',
    marginRight: 24,
  },
  scoreCenter: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
  },
  scoreValue: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1F2937',
  },
  scoreLabel: {
    fontSize: 10,
    color: '#6B7280',
    marginTop: 2,
  },
  scoreDetails: {
    flex: 1,
    gap: 12,
  },
  scoreDetail: {
    alignItems: 'center',
  },
  scoreDetailValue: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1F2937',
  },
  scoreDetailLabel: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 2,
  },
  progressCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 12,
  },
  progressIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
    gap: 8,
  },
  progressValue: {
    fontSize: 24,
    fontWeight: '700',
    color: '#10B981',
  },
  progressDate: {
    fontSize: 14,
    color: '#6B7280',
    marginBottom: 4,
  },
  nextAssessment: {
    fontSize: 14,
    color: '#6750A4',
    fontWeight: '500',
  },
  competenciesContainer: {
    gap: 16,
  },
  competencyCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  competencyHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  competencyName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    flex: 1,
  },
  priorityBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  priorityText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  competencyCategory: {
    fontSize: 12,
    color: '#6750A4',
    fontWeight: '500',
    marginBottom: 4,
  },
  competencyDescription: {
    fontSize: 14,
    color: '#4B5563',
    lineHeight: 18,
    marginBottom: 12,
  },
  levelContainer: {
    marginBottom: 12,
  },
  levelBar: {
    marginBottom: 6,
  },
  levelBackground: {
    height: 8,
    backgroundColor: '#E5E7EB',
    borderRadius: 4,
    position: 'relative',
    overflow: 'hidden',
  },
  levelFill: {
    height: '100%',
    backgroundColor: '#6750A4',
    borderRadius: 4,
  },
  targetIndicator: {
    position: 'absolute',
    top: -2,
    width: 2,
    height: 12,
    backgroundColor: '#EF4444',
    borderRadius: 1,
  },
  levelLabels: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  currentLevel: {
    fontSize: 12,
    color: '#6750A4',
    fontWeight: '500',
  },
  targetLevel: {
    fontSize: 12,
    color: '#EF4444',
    fontWeight: '500',
  },
  timeToTarget: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    gap: 4,
  },
  timeToTargetText: {
    fontSize: 12,
    color: '#6B7280',
  },
  developmentNeeds: {
    backgroundColor: '#F8F9FA',
    borderRadius: 8,
    padding: 12,
  },
  developmentTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 6,
  },
  developmentNeed: {
    fontSize: 12,
    color: '#4B5563',
    lineHeight: 16,
  },
  gapsContainer: {
    gap: 16,
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 60,
  },
  emptyStateText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
    marginTop: 16,
    textAlign: 'center',
  },
  emptyStateSubtext: {
    fontSize: 14,
    color: '#666',
    marginTop: 8,
    textAlign: 'center',
    lineHeight: 20,
  },
  gapCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  gapHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  gapSkillName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    flex: 1,
  },
  impactBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  impactText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  gapMetrics: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  gapMetric: {
    alignItems: 'center',
  },
  gapMetricLabel: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 2,
  },
  gapMetricValue: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
  },
  gapProgress: {
    marginBottom: 12,
  },
  gapProgressBar: {
    height: 6,
    backgroundColor: '#E5E7EB',
    borderRadius: 3,
    overflow: 'hidden',
    marginBottom: 4,
  },
  gapProgressFill: {
    height: '100%',
    backgroundColor: '#F59E0B',
    borderRadius: 3,
  },
  gapProgressText: {
    fontSize: 12,
    color: '#6B7280',
    textAlign: 'center',
  },
  timeEstimate: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    gap: 4,
  },
  timeEstimateText: {
    fontSize: 12,
    color: '#6B7280',
  },
  recommendedActions: {
    backgroundColor: '#F8F9FA',
    borderRadius: 8,
    padding: 12,
  },
  actionsTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 6,
  },
  actionItem: {
    fontSize: 12,
    color: '#4B5563',
    lineHeight: 16,
  },
  strengthsContainer: {
    gap: 16,
  },
  strengthCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  strengthHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  strengthSkillName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    flex: 1,
  },
  proficiencyScore: {
    alignItems: 'center',
  },
  proficiencyValue: {
    fontSize: 18,
    fontWeight: '700',
    color: '#10B981',
  },
  proficiencyLabel: {
    fontSize: 10,
    color: '#6B7280',
  },
  confidenceContainer: {
    marginBottom: 16,
  },
  confidenceLabel: {
    fontSize: 12,
    fontWeight: '500',
    color: '#374151',
    marginBottom: 6,
  },
  confidenceBar: {
    height: 6,
    backgroundColor: '#E5E7EB',
    borderRadius: 3,
    overflow: 'hidden',
    marginBottom: 4,
  },
  confidenceFill: {
    height: '100%',
    backgroundColor: '#10B981',
    borderRadius: 3,
  },
  confidenceValue: {
    fontSize: 12,
    color: '#6B7280',
    textAlign: 'right',
  },
  expertiseAreas: {
    marginBottom: 16,
  },
  expertiseTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 8,
  },
  expertiseTags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  expertiseTag: {
    backgroundColor: '#EDE7F6',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  expertiseTagText: {
    fontSize: 12,
    color: '#6750A4',
    fontWeight: '500',
  },
  leverageOpportunities: {
    backgroundColor: '#F0FDF4',
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
  },
  leverageTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: '#166534',
    marginBottom: 6,
  },
  leverageItem: {
    fontSize: 12,
    color: '#15803D',
    lineHeight: 16,
  },
  mentorshipBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#EDE7F6',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    alignSelf: 'flex-start',
    gap: 4,
  },
  mentorshipText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#6750A4',
  },
  planContainer: {
    gap: 20,
  },
  planOverview: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  planTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 8,
  },
  planTimeframe: {
    fontSize: 14,
    color: '#6750A4',
    fontWeight: '500',
    marginBottom: 4,
  },
  planCreated: {
    fontSize: 12,
    color: '#6B7280',
  },
  focusAreas: {
    gap: 12,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 8,
  },
  focusAreaCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  focusAreaHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  focusAreaName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
    flex: 1,
  },
  focusAreaPriority: {
    fontSize: 12,
    color: '#6750A4',
    fontWeight: '500',
  },
  focusAreaTarget: {
    fontSize: 12,
    color: '#10B981',
    marginBottom: 4,
  },
  focusAreaTime: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 8,
  },
  focusAreaActivities: {
    gap: 2,
  },
  activityItem: {
    fontSize: 12,
    color: '#4B5563',
    lineHeight: 16,
  },
  milestones: {
    gap: 12,
  },
  milestoneCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  milestoneHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  milestoneTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
    flex: 1,
  },
  milestoneProgress: {
    fontSize: 14,
    fontWeight: '600',
    color: '#6750A4',
  },
  milestoneDescription: {
    fontSize: 12,
    color: '#4B5563',
    marginBottom: 6,
    lineHeight: 16,
  },
  milestoneTarget: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 8,
  },
  milestoneProgressBar: {
    height: 4,
    backgroundColor: '#E5E7EB',
    borderRadius: 2,
    overflow: 'hidden',
  },
  milestoneProgressFill: {
    height: '100%',
    backgroundColor: '#6750A4',
    borderRadius: 2,
  },
});
